import { useEffect, useState, useCallback } from "react";
import { KeyRound, Check, X, Copy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Kotak password sementara — ditampilkan sekali setelah admin menyetujui/membuat */
export function TempPasswordDialog({ data, onClose }) {
  if (!data) return null;
  const copy = () => { navigator.clipboard?.writeText(`${data.username} / ${data.tempPassword}`); toast.success("Disalin"); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md border-slate-800 bg-[#0D1524] text-slate-100" data-testid="temp-password-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><KeyRound className="h-4 w-4 text-amber-400" />Password Sementara <span className="cn text-xs font-normal text-slate-500">临时密码</span></DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Sampaikan ke pengguna secara aman. Password ini hanya ditampilkan sekali; pengguna wajib menggantinya saat login pertama.</p>
        <div className="rounded border border-amber-500/30 bg-amber-500/10 p-4 text-center">
          <div className="font-mono text-sm text-slate-300" data-testid="temp-password-username">{data.username}</div>
          <div className="mt-1 font-mono text-2xl font-bold tracking-widest text-amber-300" data-testid="temp-password-value">{data.tempPassword}</div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="border-slate-700" onClick={copy} data-testid="temp-password-copy"><Copy className="mr-1.5 h-3.5 w-3.5" />Salin</Button>
          <Button className="bg-sky-600 hover:bg-sky-500" onClick={onClose} data-testid="temp-password-close">Selesai</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Daftar permintaan "lupa password" yang menunggu persetujuan admin */
export function PasswordRequests({ onTemp, refreshKey }) {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => { api.get("/password-requests?status=Menunggu").then((r) => setReqs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const approve = async (r) => {
    try { const { data } = await api.post(`/password-requests/${r.id}/approve`); onTemp(data); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const reject = async (r) => {
    try { await api.post(`/password-requests/${r.id}/reject`); toast.success(`Permintaan ${r.username} ditolak`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!reqs.length) return null;
  return (
    <div className="panel border-amber-500/30 p-4" data-testid="password-requests-panel">
      <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-bold text-amber-300">
        <ShieldAlert className="h-4 w-4" /> Permintaan Reset Password <span className="cn text-[11px] font-normal text-slate-500">密码重置申请</span>
        <span className="ml-1 rounded-full bg-amber-500 px-2 py-0.5 font-mono text-[10px] text-[#0B1220]" data-testid="password-requests-count">{reqs.length}</span>
      </h3>
      <ul className="space-y-2">
        {reqs.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs" data-testid={`password-request-${r.username}`}>
            <span className="font-mono font-bold text-amber-300">{r.username}</span>
            <span className="text-slate-300">{r.name}</span>
            <span className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] capitalize text-slate-400">{r.role}</span>
            {r.note && <span className="text-slate-500">“{r.note}”</span>}
            <span className="font-mono text-[10px] text-slate-600">{new Date(r.created_at).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="ml-auto flex gap-1">
              <Button size="sm" className="h-7 bg-emerald-600 text-xs hover:bg-emerald-500" onClick={() => approve(r)} data-testid={`password-request-approve-${r.username}`}><Check className="mr-1 h-3 w-3" />Setujui & buat password</Button>
              <Button size="sm" variant="outline" className="h-7 border-rose-500/40 text-xs text-rose-300 hover:bg-rose-500/10" onClick={() => reject(r)} data-testid={`password-request-reject-${r.username}`}><X className="mr-1 h-3 w-3" />Tolak</Button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
